import React, { forwardRef } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SearchBarProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Placeholder text for the search input
   */
  placeholder?: string;
  /**
   * Value of the search input
   */
  value?: string;
  /**
   * Change handler for the search input
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * SearchBar component - Professional search input with Figma design spec
 * Based on Figma spec: Gray background (#F8F8F8), border (#A8A8A8), yellow search icon (#F0BB44)
 */
const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  (
    { placeholder = "Tìm tên giải đấu", value, onChange, className, ...props },
    ref,
  ) => {
    return (
      <div className={cn("relative w-full", className)}>
        <div className="relative">
          {/* Search Icon */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <Search className="h-5 w-5 text-yellow-500" strokeWidth={2} />
          </div>

          {/* Input Field */}
          <input
            ref={ref}
            type="text"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={cn(
              // Base styles
              "w-full h-12 pl-12 pr-4 py-3",

              // Background and border - matching Figma spec
              "bg-neutral-100 border border-neutral-400",
              "rounded-[10px]",

              // Typography
              "mebi-body-medium-regular text-neutral-900 placeholder:text-neutral-500",

              // Focus states
              "focus:outline-none focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500",

              // Hover states
              "hover:border-neutral-500",

              // Disabled states
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50",

              // Transition
              "transition-all duration-200",
            )}
            {...props}
          />
        </div>
      </div>
    );
  },
);

SearchBar.displayName = "SearchBar";

export { SearchBar };
